import { IAbstractInitialState } from "../../types/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { TItemsAddsSlider } from "../../types/addsSlider";
import {
  getDefaultCovers,
  getHomePageSetting,
  getPreRoll,
  getSEOSettings,
} from "../../api/asyncThuncks/settings";
import {
  TAudioPlayerController,
  TPlaylist,
  TRadioPlayerController,
} from "../../types/playerController";
import { IPlaylist, IPlaylists } from "../../types/playlists";
import { addRemoveLike } from "../../api/asyncThuncks/audioTracks";
import { getHealthCheck } from "../../api/asyncThuncks/healthCheck";
import { HealthStatusEnum } from "../../common/enums";
import {
  TAudioPreRoll,
  TSEOSettings,
  TSEOSettingsDefault,
} from "../../types/settings";
import {
  TContestByPublickUrl,
  TContestForHomePage,
} from "../../types/contests";
import { TrackState } from "../../types/audioTrack";
import {
  addRemoveContestsLike,
  artistsByContest,
  detailByContest,
  favoriteByContest,
} from "../../api/asyncThuncks/contestsCards";
import feedback from "../../api/asyncThuncks/feedback";
import { getSitemap } from "../../api/asyncThuncks/sitemap";
import getConfirmationByMessage from "../../api/asyncThuncks/confirmationByMessage";

interface IInitialState extends IAbstractInitialState {
  radioPlayer: TRadioPlayerController;
  audioPlayer: TAudioPlayerController;
  audioPreRoll: TAudioPreRoll;
  idPlayingTrack: TPlaylist;
  addsSliderHomePage: TItemsAddsSlider[] | null;
  contestsHomePagUrls: TContestByPublickUrl[] | null;
  playlistHomePage: IPlaylists[] | null;
  healthCheck: HealthStatusEnum | null;
  SEOSettingsDefault: TSEOSettingsDefault | null;
  SEOSettings: TSEOSettings;
  defaultCover: string;
  contestHomePage: TContestForHomePage;
  feedbackModal: boolean;
  successMessageModal: boolean;
  voteModal: boolean;
  confirmationByMessageModal: boolean;
  successMessageByVoteModal: boolean;
  errorMessageByVoteModal: boolean;
  sitemap: any;
}

const initialState: IInitialState = {
  loading: "idle",
  healthCheck: null,
  SEOSettingsDefault: null,
  radioPlayer: {
    viewPlayer: false,
    overTop: false,
    radio: false,
    volume: 50,
    buttonState: "play",
    player: null,
    metadata: "",
    currentUrl: "",
    playForTrack: false,
    preRollPlayed: null,
  },
  audioPlayer: {
    viewPlayer: false,
    playForTrack: false,
    overTop: false,
    buttonState: "play",
    currentUrl: "",
    trackState: null,
  },
  idPlayingTrack: {
    idTrack: "",
    cover: "",
    name: "",
    artist: "",
    isLiked: false,
    trackLink: "",
  },
  SEOSettings: {
    appTitle: "",
    appDescription: "",
    appImg: "",
  },
  contestHomePage: {
    details: null,
    favorites: {
      audioTracks: null,
      name: "",
    },
    artists: null,
  },
  audioPreRoll: {
    audioPreRollCreatedAt: null,
    audioPreRollFileName: null,
    audioPreRollLength: null,
    audioPreRollPath: null,
  },
  defaultCover: "",
  feedbackModal: false,
  successMessageModal: false,
  voteModal: false,
  confirmationByMessageModal: false,
  successMessageByVoteModal: false,
  errorMessageByVoteModal: false,
  addsSliderHomePage: null,
  contestsHomePagUrls: null,
  playlistHomePage: null,
  error: "",
  sitemap: "",
};

const mainSlice = createSlice({
  name: "mainSlice",
  initialState,
  reducers: {
    setViewRadioPlayer(state, { payload }: PayloadAction<boolean>) {
      state.radioPlayer.viewPlayer = payload;
    },
    setRadioOverTop(state, { payload }: PayloadAction<boolean>) {
      state.radioPlayer.overTop = payload;
    },
    setVolumePlayer(state, { payload }: PayloadAction<number>) {
      state.radioPlayer.volume = payload;
    },
    setPreRollPlayed(state, { payload }: PayloadAction<boolean>) {
      state.radioPlayer.preRollPlayed = payload;
    },
    setButtonState(state, { payload }: PayloadAction<string>) {
      state.radioPlayer.buttonState = payload;
    },
    setPlayerState(state, { payload }: PayloadAction<any>) {
      state.radioPlayer.player = payload;
    },
    setMetadataState(state, { payload }: PayloadAction<string>) {
      state.radioPlayer.metadata = payload;
    },
    setCurrentUrl(state, { payload }: PayloadAction<string>) {
      state.radioPlayer.currentUrl = payload;
    },
    setPlayRadio(state, { payload }: PayloadAction<boolean>) {
      state.radioPlayer.radio = payload;
    },
    setPlayTrack(state, { payload }: PayloadAction<boolean>) {
      state.audioPlayer.playForTrack = payload;
    },
    setViewAudioPlayer(state, { payload }: PayloadAction<boolean>) {
      state.audioPlayer.viewPlayer = payload;
    },
    setAudioOverTop(state, { payload }: PayloadAction<boolean>) {
      state.audioPlayer.overTop = payload;
    },
    setIdTrack(
      state,
      {
        payload: { idTrack, name, artist, cover, isLiked },
      }: PayloadAction<TPlaylist>
    ) {
      state.idPlayingTrack.idTrack = idTrack;
      state.idPlayingTrack.cover = cover;
      state.idPlayingTrack.name = name;
      state.idPlayingTrack.artist = artist;
      state.idPlayingTrack.isLiked = isLiked;
    },
    setTrackLink(state, { payload }: PayloadAction<string>) {
      state.idPlayingTrack.trackLink = payload;
    },
    setTrackState(state, { payload }: PayloadAction<TrackState>) {
      state.audioPlayer.trackState = payload;
    },
    setSEOSettings(
      state,
      {
        payload: { appTitle, appDescription, appImg },
      }: PayloadAction<TSEOSettings>
    ) {
      state.SEOSettings.appTitle = appTitle;
      if (appDescription) {
        let trimmedDescription = appDescription.slice(0, 130);

        if (appDescription.length > 130) {
          trimmedDescription += "...";
        }
        state.SEOSettings.appDescription = trimmedDescription;
      } else {
        state.SEOSettings.appDescription = "";
      }
      state.SEOSettings.appImg = appImg;
    },
    setFeedbackModal(state, { payload }: PayloadAction<boolean>) {
      state.feedbackModal = payload;
    },
    setSuccessMessageModal(state, { payload }: PayloadAction<boolean>) {
      state.successMessageModal = payload;
    },
    setVoteModal(state, { payload }: PayloadAction<boolean>) {
      state.voteModal = payload;
    },
    setConfirmationByMessageModal(state, { payload }: PayloadAction<boolean>) {
      state.confirmationByMessageModal = payload;
    },

    setSuccessMessageByVoteModal(state, { payload }: PayloadAction<boolean>) {
      state.successMessageByVoteModal = payload;
    },

    setErrorMessageByVoteModal(state, { payload }: PayloadAction<boolean>) {
      state.errorMessageByVoteModal = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHomePageSetting.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getHomePageSetting.fulfilled, (state, { payload }) => {
        state.addsSliderHomePage = payload.banners.sort(function (a, b) {
          return a.index - b.index;
        });
        state.contestsHomePagUrls = payload.contests;
        state.playlistHomePage = [...payload.playlists];
        state.loading = "idle";
      })
      .addCase(getHomePageSetting.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(
        addRemoveLike.pending,
        (
          state,
          {
            meta: {
              arg: { id },
            },
          }
        ) => {
          // if (state.playlistHomePage) {
          //   state.playlistHomePage = {
          //     ...state.playlistHomePage,
          //     audioTracks: state.playlistHomePage.audioTracks.map(
          //       (audioTrack) => {
          //         if (audioTrack.id === id) {
          //           audioTrack.isLiked = !audioTrack.isLiked;
          //           audioTrack.likes = audioTrack.isLiked
          //             ? audioTrack.likes + 1
          //             : audioTrack.likes - 1;
          //         }
          //         return audioTrack;
          //       }
          //     ),
          //   };
          // }
          state.loading = "pending";
        }
      )
      .addCase(addRemoveLike.fulfilled, (state, { payload }) => {
        state.loading = "idle";
      })
      .addCase(
        addRemoveLike.rejected,
        (
          state,
          {
            meta: {
              arg: { id },
            },
          }
        ) => {
          // if (state.playlistHomePage) {
          //   state.playlistHomePage = {
          //     ...state.playlistHomePage,
          //     audioTracks: state.playlistHomePage.audioTracks.map(
          //       (audioTrack) => {
          //         if (audioTrack.id === id) {
          //           audioTrack.isLiked = false;
          //           audioTrack.likes = audioTrack.likes - 1;
          //         }
          //         return audioTrack;
          //       }
          //     ),
          //   };
          // }
        }
      )
      .addCase(getHealthCheck.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getHealthCheck.fulfilled, (state, { payload }) => {
        state.healthCheck = payload;
        state.loading = "idle";
      })
      .addCase(getHealthCheck.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getSEOSettings.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getSEOSettings.fulfilled, (state, { payload }) => {
        state.loading = "idle";
        state.SEOSettingsDefault = payload;
      })
      .addCase(getSEOSettings.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getDefaultCovers.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getDefaultCovers.fulfilled, (state, { payload }) => {
        state.defaultCover = payload;
        state.loading = "idle";
      })
      .addCase(getDefaultCovers.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(detailByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(detailByContest.fulfilled, (state, { payload }) => {
        state.contestHomePage.details = payload;
        state.loading = "idle";
      })
      .addCase(detailByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(favoriteByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(favoriteByContest.fulfilled, (state, { payload }) => {
        state.contestHomePage.favorites.audioTracks = payload.audioTracks;
        state.contestHomePage.favorites.name = payload.name;
        state.loading = "idle";
      })
      .addCase(favoriteByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.contestHomePage.favorites = { audioTracks: null, name: "" };
        state.loading = "failed";
      })
      .addCase(artistsByContest.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(artistsByContest.fulfilled, (state, { payload }) => {
        state.contestHomePage.artists = [...payload];
        state.loading = "idle";
      })
      .addCase(artistsByContest.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(
        addRemoveContestsLike.pending,
        (
          state,
          {
            meta: {
              arg: { audioTrackId },
            },
          }
        ) => {
          state.loading = "pending";

          if (state.contestHomePage.favorites.audioTracks) {
            state.contestHomePage = {
              ...state.contestHomePage,
              favorites: {
                ...state.contestHomePage.favorites,
                audioTracks: state.contestHomePage.favorites.audioTracks.map(
                  (audioTrack) => {
                    if (audioTrack.id === audioTrackId) {
                      return {
                        ...audioTrack,
                        isLiked: !audioTrack.isLiked,
                        likes: audioTrack.isLiked
                          ? audioTrack.likes - 1
                          : audioTrack.likes + 1,
                      };
                    } else {
                      return audioTrack;
                    }
                  }
                ),
              },
            };
          }
        }
      )
      .addCase(addRemoveContestsLike.fulfilled, (state) => {
        state.loading = "idle";
      })
      .addCase(
        addRemoveContestsLike.rejected,
        (
          state,
          {
            meta: {
              arg: { audioTrackId },
            },
          }
        ) => {
          if (state.contestHomePage.favorites.audioTracks) {
            state.contestHomePage = {
              ...state.contestHomePage,
              favorites: {
                ...state.contestHomePage.favorites,
                audioTracks: state.contestHomePage.favorites.audioTracks.map(
                  (audioTrack) => {
                    if (audioTrack.id === audioTrackId) {
                      return {
                        ...audioTrack,
                        isLiked: !audioTrack.isLiked,
                        likes: audioTrack.isLiked
                          ? audioTrack.likes - 1
                          : audioTrack.likes + 1,
                      };
                    } else {
                      return audioTrack;
                    }
                  }
                ),
              },
            };
          }
        }
      )
      .addCase(getPreRoll.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getPreRoll.fulfilled, (state, { payload }) => {
        state.audioPreRoll = payload;
        state.loading = "idle";
      })
      .addCase(getPreRoll.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(feedback.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(feedback.fulfilled, (state, { payload }) => {
        state.loading = "idle";
      })
      .addCase(feedback.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getSitemap.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getSitemap.fulfilled, (state, { payload }) => {
        state.sitemap = payload;
        state.loading = "idle";
      })
      .addCase(getSitemap.rejected, (state, { error }) => {
        if (error.message) state.error = error.message;
        state.loading = "failed";
      })
      .addCase(getConfirmationByMessage.fulfilled, (state, action) => {
        const { audioTrackId } = action.meta.arg;
        if (state.contestHomePage.favorites.audioTracks) {
          state.contestHomePage.favorites.audioTracks = state.contestHomePage.favorites.audioTracks.map((track) => {
            if (track.id === audioTrackId) {
              return { ...track, likes: track.likes + 1, isLiked: true, voices: String(Number(track.voices || '0') + 1) };
            }
            return track;
          });
        }
      });
  },
});

export const selectMainState = (state: RootState): IInitialState => state.main;

export const {
  setViewRadioPlayer,
  setViewAudioPlayer,
  setRadioOverTop,
  setAudioOverTop,
  setVolumePlayer,
  setPreRollPlayed,
  setButtonState,
  setPlayerState,
  setMetadataState,
  setCurrentUrl,
  setPlayRadio,
  setPlayTrack,
  setIdTrack,
  setTrackState,
  setTrackLink,
  setSEOSettings,
  setFeedbackModal,
  setSuccessMessageModal,
  setVoteModal,
  setConfirmationByMessageModal,
  setSuccessMessageByVoteModal,
  setErrorMessageByVoteModal,
} = mainSlice.actions;

export default mainSlice.reducer;
